import { defaultSEO } from "@/config/seoConfig";

function toISODate(date?: string | Date): string | undefined {
  if (!date) {
    return undefined;
  }

  return new Date(date).toISOString().split("T")[0];
}

export interface JobLocation {
  streetAddress?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
}

export interface JobData {
  title?: string;
  description?: string;
  datePosted?: string | Date;
  validThrough?: string | Date;
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency?: string;
  isRemote?: boolean;
  applicantCountry?: string;
  hiringOrganization?: {
    name?: string;
    sameAs?: string;
  };
  location?: JobLocation;
}

export type JobPostingSchemaInput = JobData;

function getBaseSalary(job?: JobData) {
  const hasRange =
    typeof job?.salaryMin === "number" || typeof job?.salaryMax === "number";

  if (!hasRange) {
    return undefined;
  }

  const minValue = typeof job?.salaryMin === "number" ? job.salaryMin : undefined;
  const maxValue = typeof job?.salaryMax === "number" ? job.salaryMax : undefined;

  return {
    "@type": "MonetaryAmount",
    currency: job?.salaryCurrency || "USD",
    value: {
      "@type": "QuantitativeValue",
      ...(minValue ? { minValue } : {}),
      ...(maxValue ? { maxValue } : {}),
      unitText: "YEAR",
    },
  };
}

/**
 * Returns JobPosting schema JSON-LD for a job object.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function generateJobPostingSchema(job?: JobData): Record<string, any> {
  const isRemote = Boolean(job?.isRemote);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const schema: Record<string, any> = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: job?.title,
    description: job?.description,
    datePosted: toISODate(job?.datePosted),
    validThrough: toISODate(job?.validThrough),
    employmentType: "FULL_TIME",
    hiringOrganization: {
      "@type": "Organization",
      name: job?.hiringOrganization?.name || defaultSEO.siteName,
      sameAs: job?.hiringOrganization?.sameAs || defaultSEO.siteUrl,
    },
    ...(isRemote
      ? {
          applicantLocationRequirements: {
            "@type": "Country",
            name: job?.applicantCountry || "US",
          },
          jobLocationType: "TELECOMMUTE",
        }
      : {
          jobLocation: {
            "@type": "Place",
            address: {
              "@type": "PostalAddress",
              streetAddress: job?.location?.streetAddress,
              addressLocality: job?.location?.city,
              addressRegion: job?.location?.state,
              postalCode: job?.location?.postalCode,
              addressCountry: job?.location?.country || "US",
            },
          },
        }),
  };

  const baseSalary = getBaseSalary(job);
  if (baseSalary) {
    schema.baseSalary = baseSalary;
  }

  return schema;
}
